import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  DestroyRef,
  DOCUMENT,
  effect,
  EnvironmentInjector,
  inject,
  Injectable,
  inputBinding,
  outputBinding,
  untracked
} from '@angular/core';
import { ToastComponent } from '@core/components';
import {
  TOAST_DEFAULTS,
  ToastOptions,
  ToastPlacement,
  ToastRef,
  ToastShorthandOptions,
  ToastSupport
} from '@core/components/toast';
import { LightboxService } from '@core/services';

interface ActiveToast {
  readonly ref: ComponentRef<ToastComponent>;
  readonly host: HTMLElement;
  readonly container: HTMLElement;
  readonly placement: ToastPlacement;
  height: number;
  offset: number;
  index: number;
  stackHeight: number;
  stackCount: number;
}

type ActiveMap = Map<string, ActiveToast>;

interface StackState {
  height: number;
  count: number;
  offset: number;
  index: number;
}

/**
 * App-wide toast controller.
 *
 * Performance model:
 * - Nothing is rendered until a toast is requested; each toast is created on demand,
 *   attached directly to ApplicationRef (no container component), and destroyed on close.
 * - OnPush + signals; content is sanitized once per toast.
 * - Toasts stack independently per `placement`.
 * - One shared ResizeObserver feeds stacking. It reads sizes the browser already computed
 *   (no forced layout) and only writes custom properties that actually changed. Those writes
 *   move toasts but never resize them, so they can't re-trigger the observer.
 * - Enter/exit is pure CSS (opacity/translate/scale); no animation runtime.
 */
@Injectable({ providedIn: 'root' })
export class Toast {
  private readonly _appRef = inject(ApplicationRef);
  private readonly _injector = inject(EnvironmentInjector);
  private readonly _document = inject(DOCUMENT);
  private readonly _defaults = inject(TOAST_DEFAULTS);
  private readonly _lightboxSvc = inject(LightboxService);
  /** Insertion-ordered: within a placement, the first entry sits at the edge, later ones stack away from it. */
  private readonly _active: ActiveMap = new Map();
  private readonly _byHost = new WeakMap<Element, ActiveToast>();
  private _observer: ResizeObserver | null = null;
  private _sequence = 0;

  constructor() {
    effect(() => {
      if (!this._lightboxSvc.$state().isOpen) {
        untracked(() => this._dismissLightboxToasts());
      }
    });
    inject(DestroyRef).onDestroy(() => {
      this._observer?.disconnect();
      for (const id of [...this._active.keys()]) {
        this._remove(id);
      }
    });
  }

  alert(
    title: string,
    body: string,
    options: Partial<Omit<ToastShorthandOptions, 'support'>> & ToastSupport = {
      shouldShowHelpCenter: true,
      shouldShowServiceDesk: false
    }
  ): ToastRef {
    const { shouldShowHelpCenter = true, shouldShowServiceDesk = false, ...opts } = options;
    const support = { shouldShowHelpCenter, shouldShowServiceDesk };
    return this.show({ ...opts, title, body, role: 'alert', support });
  }

  warning(title: string, body: string, options?: Omit<ToastShorthandOptions, 'support'>): ToastRef {
    return this.show({ ...options, title, body, role: 'warning' });
  }

  info(title: string, body: string, options?: Omit<ToastShorthandOptions, 'support'>): ToastRef {
    return this.show({ ...options, title, body, role: 'info' });
  }

  success(title: string, body: string, options?: Omit<ToastShorthandOptions, 'support'>): ToastRef {
    return this.show({ ...options, title, body, role: 'success' });
  }

  show(options: ToastOptions): ToastRef {
    const id = `oke-toast-${++this._sequence}`;
    const {
      title,
      body,
      role = this._defaults.role,
      popover = this._defaults.popover,
      animation = this._defaults.animation,
      classNames = this._defaults.classNames,
      autoDismiss = this._defaults.autoDismiss,
      placement = this._defaults.placement,
      support
    } = options;
    let resolveClosed!: () => void;
    const closed = new Promise<void>(resolve => (resolveClosed = resolve));
    const host = this._document.createElement('output');
    const ref = createComponent(ToastComponent, {
      environmentInjector: this._injector,
      hostElement: host,
      bindings: [
        inputBinding('id', () => id),
        inputBinding('title', () => title),
        inputBinding('body', () => body),
        inputBinding('role', () => role),
        inputBinding('popover', () => popover),
        inputBinding('animation', () => animation),
        inputBinding('classNames', () => classNames),
        inputBinding('autoDismiss', () => autoDismiss),
        inputBinding('placement', () => placement),
        inputBinding('support', () => (support ? support : null)),
        outputBinding('closed', () => {
          this._remove(id);
          resolveClosed();
        })
      ]
    });
    const entry: ActiveToast = {
      ref,
      host,
      container: this._getContainer(),
      placement,
      height: 0,
      offset: -1,
      index: -1,
      stackHeight: -1,
      stackCount: -1
    };
    this._active.set(id, entry);
    this._byHost.set(host, entry);

    this._appRef.attachView(ref.hostView);
    entry.container.append(host);

    this._observer ??= new ResizeObserver(entries => this._measure(entries));
    this._observer.observe(host);
    this._restack();
    if (this._lightboxSvc.$isHidden()) {
      setTimeout(() => {
        this._lightboxSvc.reveal();
      }, 1000);
    }

    return {
      id,
      closed,
      dismiss: () => this._active.get(id)?.ref.instance.dismiss()
    };
  }

  dismiss(id: string): void {
    this._active.get(id)?.ref.instance.dismiss();
  }

  dismissAll(): Toast {
    for (const { ref } of this._active.values()) {
      ref.instance.dismiss();
    }
    return this;
  }

  /** A modal `<dialog>` makes everything outside it inert, so toasts must live inside it while it's shown. */
  private _getContainer(): HTMLElement {
    const dialog = this._lightboxSvc.hostElement;
    const isLightboxShown =
      this._lightboxSvc.$isOpen() && this._lightboxSvc.$state().isOpen && !this._lightboxSvc.$isHidden();
    return dialog && isLightboxShown ? dialog : this._document.body;
  }

  private _dismissLightboxToasts(): void {
    for (const { ref, container } of this._active.values()) {
      if (container !== this._document.body) {
        ref.instance.dismiss();
      }
    }
  }

  private _measure(entries: ResizeObserverEntry[]): void {
    let isChanged = false;
    for (const { target, borderBoxSize } of entries) {
      const entry = this._byHost.get(target);
      const height = borderBoxSize[0]?.blockSize ?? 0;
      if (entry && entry.height !== height) {
        entry.height = height;
        isChanged = true;
      }
    }
    if (isChanged) {
      this._restack();
    }
  }

  private _restack(): void {
    const stacks = new Map<ToastPlacement, StackState>();
    for (const { placement, height } of this._active.values()) {
      let stack = stacks.get(placement);
      if (!stack) {
        stack = { height: 0, count: 0, offset: 0, index: 0 };
        stacks.set(placement, stack);
      }
      if (height > 0) {
        stack.height += height;
        stack.count++;
      }
    }

    for (const entry of this._active.values()) {
      const stack = stacks.get(entry.placement)!;
      const stackHeight = stack.height;
      const stackCount = Math.max(stack.count, 1);
      const { offset, index } = stack;
      if (entry.stackHeight !== stackHeight) {
        entry.host.style.setProperty('--toast-stack-height', `${stackHeight}px`);
        entry.stackHeight = stackHeight;
      }
      if (entry.stackCount !== stackCount) {
        entry.host.style.setProperty('--toast-stack-count', `${stackCount}`);
        entry.stackCount = stackCount;
      }
      if (entry.offset !== offset) {
        entry.host.style.setProperty('--toast-offset', `${offset}px`);
        entry.offset = offset;
      }
      if (entry.index !== index) {
        entry.host.style.setProperty('--toast-index', `${index}`);
        entry.index = index;
      }
      if (entry.height > 0) {
        stack.offset += entry.height;
        stack.index++;
      }
    }
  }

  private _remove(id: string): void {
    const entry = this._active.get(id);
    if (!entry) {
      return;
    }
    this._active.delete(id);
    this._observer?.unobserve(entry.host);
    entry.ref.destroy(); // also detaches the view from ApplicationRef
    entry.host.remove();
    this._restack();
  }
}
